/**
 * How a field mapping turns a Workfront value into its target (`wf_field_mapping.rule`).
 * A closed set: new behaviour is a new {@link WorkfrontComputeFn}, never a free-form rule.
 */
export enum WorkfrontMappingRule {
	/** Copy the value, typed to the target. */
	DIRECT = 'direct',
	/** Through a {@link WorkfrontLookup}, or for an axis target through the `workfront` value mappings. */
	LOOKUP = 'lookup',
	/** The asset type, from the (Classification, Asset Detail) pair. */
	TABLE = 'table',
	/** The description, from the configured form sections. */
	COMPOSE = 'compose',
	/** A named computation over the object and its bundle. */
	COMPUTE = 'compute',
	/** A value ATLAS creates when it does not exist yet (a campaign). */
	CREATE = 'create',
	/** A link built from a configured host. */
	LINK = 'link',
}

/** The four mappings, as an error, a draft change and a published row name them. */
export enum WorkfrontMappingKind {
	FIELD = 'field',
	VALUE = 'value',
	ASSET_TYPE = 'asset_type',
	LOOKUP = 'lookup',
}

/** Where a field mapping reads its value: a custom field, or a standard Workfront field. */
export enum WorkfrontSourceKind {
	PARAMETER = 'parameter',
	STANDARD = 'standard',
}

/** The code-to-code tables an admin edits (`wf_lookup.lookup`). */
export enum WorkfrontLookup {
	COST_LEVEL_TO_COMPLEXITY = 'cost_level_to_complexity',
	STATUS_TO_CLOSED = 'status_to_closed',
	TEMPLATE_TASK_TO_STAGE = 'template_task_to_stage',
	TEAM_TO_AGENCY = 'team_to_agency',
	LANGUAGE_TO_LOCALE = 'language_to_locale',
	/** An ATLAS geography value code, or `*` for the default, to the user ID of a PM. */
	REGION_TO_PM = 'region_to_pm',
}

/** The `region_to_pm` source that matches a brief no other rule matches. */
export const DEFAULT_PM_RULE_SOURCE = '*'

/** How every PM rule's draft key starts: a lookup row's key is `<lookup>|<source>`. */
export const PM_RULE_KEY_PREFIX = `${WorkfrontLookup.REGION_TO_PM}|`

/** Each lookup as an admin reads it on the Lookups screen and in error texts. */
export const WORKFRONT_LOOKUP_LABELS: Record<WorkfrontLookup, string> = {
	[WorkfrontLookup.COST_LEVEL_TO_COMPLEXITY]: 'Cost level to complexity',
	[WorkfrontLookup.STATUS_TO_CLOSED]: 'Status to closed',
	[WorkfrontLookup.TEMPLATE_TASK_TO_STAGE]: 'Template task to stage',
	[WorkfrontLookup.TEAM_TO_AGENCY]: 'Team to agency',
	[WorkfrontLookup.LANGUAGE_TO_LOCALE]: 'Language to locale',
	[WorkfrontLookup.REGION_TO_PM]: 'Region to PM',
}

/** The named computations a `compute` field mapping may call. */
export enum WorkfrontComputeFn {
	/** The sum of the generator issues' created-asset counts. */
	PLANNED_ASSET_COUNT = 'planned_asset_count',
	IS_ADAPTATION = 'is_adaptation',
	/** The PromoMats document numbers, in field order, without repeats. */
	ORIGINAL_CODES = 'original_codes',
	/** Vault document links found in the object's rich text. */
	REFERENCED_DOCUMENTS = 'referenced_documents',
	/** The object's Workfront documents, each as a link to its page in Workfront. */
	RELATED_LINKS = 'related_links',
	/** A language plus the object's country, as an ATLAS locale. */
	LOCALE = 'locale',
	/** True when a Workfront answer is the configured "yes" value. */
	YES_NO = 'yes_no',
}

/** Why a bundle is re-mapped (`workfront_bundle.remap_requested.cause`). */
export enum WorkfrontRemapCause {
	/** A new mapping version was published. */
	REMAP = 'remap',
	/** A value mapping that held objects were waiting for was added. */
	FIX = 'fix',
	ADMIN = 'admin',
	RETRY = 'retry',
}

/** Which canonical object a target belongs to. */
export enum WorkfrontTargetLevel {
	PARENT = 'parent',
	CHILD = 'child',
	TASK = 'task',
	COMMENT = 'comment',
	DOCUMENT = 'document',
}

/** The shape of a target's value, which save-time validation checks a source against. */
export enum WorkfrontTargetType {
	STRING = 'string',
	HTML = 'html',
	/** A calendar date, `YYYY-MM-DD`. */
	DATE = 'date',
	/** An instant, ISO 8601 in UTC. */
	DATETIME = 'datetime',
	MONEY = 'money',
	INTEGER = 'integer',
	BOOLEAN = 'boolean',
	/** Values of a classification axis, named by the target's `axis`. */
	AXIS = 'axis',
	/** A member of a code-owned enum, named by the target's `enumName`. */
	ENUM = 'enum',
	/** The composed description sections. */
	SECTIONS = 'sections',
	/** A list of structured items, built by a computation. */
	LIST = 'list',
}

/** How many values a target takes. */
export enum WorkfrontTargetCardinality {
	ONE = 'one',
	MANY = 'many',
}

/** How a Workfront label came to be mapped to an ATLAS value. */
export enum WorkfrontValueMatch {
	EXACT = 'exact',
	/** A close name match, proposed for review. */
	PROPOSED = 'proposed',
	MANUAL = 'manual',
	UNMATCHED = 'unmatched',
}

/** What a draft change does to the published row with its key. */
export enum WorkfrontDraftOp {
	UPSERT = 'upsert',
	DELETE = 'delete',
}

/** How well an import's described ID matches an ID of the tenant's Workfront. */
export enum WorkfrontImportMatch {
	/** The ID exists as it is. */
	SAME = 'same',
	EXACT = 'exact',
	LIKELY = 'likely',
	NOT_FOUND = 'not_found',
}
